import LeadEditor from "pages/shared/lead-editor/LeadEditor"
import Loading from "pages/shared/loading/Loading"
import { useContext, useEffect, useState } from "react"
import { useTranslation } from 'react-i18next';
import { useErrorBoundary } from "react-error-boundary"
import { useNavigate, useParams } from "react-router"
import { NotificationContext } from "shared/NotificationContext"
import { useBackofficeApi } from 'shared/useBackofficeApi';
import { Lead, LeadCreation, NotificationType } from "shared/topchef.types"

const BackofficeValidateLead = () => {

  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const notificationContext = useContext(NotificationContext);
  const { triggerNotification } = notificationContext;

  const params = useParams();
  const leadId: string = params.leadId || '';

  const [isReady, setReady] = useState<boolean>(false);
  const [currentLead, setCurrentLead] = useState<Lead | null>(null);
  
  const { showBoundary } = useErrorBoundary();
  const backofficeApi = useBackofficeApi();

  useEffect(() => {
    setReady(false);
  }, [leadId, backofficeApi.country]);

  useEffect(() => { // eslint-disable-line react-hooks/exhaustive-deps

    async function loadLead() {
      try {
        const lead: Lead = await backofficeApi.loadLeadToValidate(leadId);
        setCurrentLead(lead);
        setReady(true);
      } catch (error: any) { 
        const newError = new Error(t('apiErrors.loadLeadToValidate', { details: error.message }) as string);
        triggerNotification({type: NotificationType.error, message : newError.message}); 
        showBoundary(newError);
      } 
    }

    if (isReady) {
      return;
    }

    loadLead();

  }, [isReady, leadId, backofficeApi, showBoundary, triggerNotification]); // eslint-disable-line react-hooks/exhaustive-deps


  const denyLead = async () => {
    try {
      await backofficeApi.refuseLead(leadId);
      triggerNotification({type: NotificationType.information, message : t('validation.briefRefused')});
      navigate('/backoffice/leads/to-validate');
    } catch (error: any) {
      const newError = new Error(t('apiErrors.validateLead', { details: error.details }) as string);
      triggerNotification({type: NotificationType.error, message : newError.message});
      showBoundary(newError);
    }
  }

  const validateLead = async (leadCreation: LeadCreation) => {
    try {
      await backofficeApi.validateLead(leadId, leadCreation!);
      triggerNotification({type: NotificationType.information, message : t('validation.briefValidated')});
      navigate('/backoffice/leads/to-validate');
    } catch (error: any) {
      const newError = new Error(t('apiErrors.validateLead', { details: error.message }) as string);
      triggerNotification({type: NotificationType.error, message : `${newError.message}`});
      showBoundary(newError);
    }
  };

  const leadToLeadCreation = (lead: Lead): LeadCreation => {
    return {
      type: lead.type,
      targetDate: lead.targetDate.toString(),
      startTime: lead.startTime ?? '',
      endTime: lead.endTime ?? '',
      guests: lead.guests,
      budget: lead.budget,
      region: lead.region,
      city: lead.city,
      description: lead.description,
      contactName: lead.contactName,
      contactCompany: lead.contactCompany,
      contactEmailAddress: lead.contactEmailAddress,
      contactMobilePhone: lead.contactMobilePhone,
      contactLandlinePhone: lead.contactLandlinePhone
    };
  }

  if (!isReady) {
    return (
      <Loading />
    );
  }



  const updateLeadState= (name: string, value: any) => {
    setCurrentLead((prevState) => ({
      ...prevState,
      [name]: value,
    } as Lead));
  }

  return (
      <div className="container p-5 overflow-auto backoffice-lead-to-validate-details" >
      <h4 className="mb-3 text-center">{t('validation.validateBrief')}</h4>

      <div className="row mt-4">
        <LeadEditor formData={leadToLeadCreation(currentLead!)} onDataChange={updateLeadState} onSubmit={validateLead} submitButtonLabel={t('validation.validateBrief')} country={backofficeApi.country} />
      </div>

      <div className="text-center">
        <button className="btn btn-primary m-3 refuse-btn" onClick={denyLead}>{t('validation.refuseBrief')}</button> 
      </div>
    
    </div>
  );

}

export default BackofficeValidateLead;
